/**
 * Part Schedule → "Not from ERP" tab.
 *
 * Parts on open scheduling-agreement lines that the ERP sends no firm window
 * for. Each runs on the tenant default until a customer admin sets a manual
 * value here; the moment the ERP starts sending the part, the ERP value wins
 * and the part moves to the "From ERP" tab (its manual value kept as
 * superseded). Read-only for non-admins.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { purchasingAPI } from '../../lib/api';
import type { PartNotFromErp } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { PaginationControls } from './PartScheduleTableControls';
import ManualFirmDaysDialog from './ManualFirmDaysDialog';
import PartScheduleFiltersCard from './PartScheduleFiltersCard';

const FIRM_SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'default', label: 'Default' },
  { value: 'manual', label: 'Manual' },
];

/** Debounce a free-text value so each keystroke does not hit the server. */
function useDebounced(value: string, ms = 300): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

const PAGE_SIZE = 50;

export default function NotFromErpTab() {
  const { user } = useAuth();
  const { formatDate } = useFormatters();
  // Mirrors the server gate (is_otif_admin): superuser, Helix, or a CUSTOMER
  // admin — a supplier admin who reaches the page by URL gets no pencil.
  const canEdit = Boolean(
    user?.is_superuser
    || user?.platform === 'HELIX'
    || (user?.is_company_admin && user?.platform === 'CUSTOMER'),
  );

  const [rows, setRows] = useState<PartNotFromErp[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Same filters as the From ERP tab; the status slot is Default/Manual here.
  const [search, setSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [firmSource, setFirmSource] = useState('all');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<PartNotFromErp | null>(null);
  const debouncedSearch = useDebounced(search);
  const debouncedSupplierSearch = useDebounced(supplierSearch);
  // Names may contain commas, so the multi-select travels as JSON (as on the ERP tab).
  const supplierFilterKey = supplierFilter.length ? JSON.stringify(supplierFilter) : '';
  // Progressive typing fires overlapping requests; only the newest may land.
  const requestRef = useRef(0);

  useEffect(() => {
    purchasingAPI.getPartsNotFromErpSupplierOptions()
      .then((res) => setSupplierOptions(res.data.suppliers ?? []))
      .catch(() => setSupplierOptions([]));
  }, []);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page, page_size: PAGE_SIZE };
      if (debouncedSearch) params['search'] = debouncedSearch;
      if (debouncedSupplierSearch) params['supplier_search'] = debouncedSupplierSearch;
      if (supplierFilterKey) params['supplier_filter'] = supplierFilterKey;
      if (firmSource !== 'all') params['firm_source'] = firmSource;
      const res = await purchasingAPI.getPartsNotFromErp(params);
      if (requestId !== requestRef.current) return;
      setRows(res.data.results);
      setTotal(res.data.count);
    } catch (err: unknown) {
      if (requestId !== requestRef.current) return;
      console.error('Failed to load parts not from ERP:', err);
      setError('Failed to load parts. Try again.');
      toast.error('Failed to load parts not from ERP');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [page, debouncedSearch, debouncedSupplierSearch, supplierFilterKey, firmSource]);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onSaved = (saved: PartNotFromErp) => {
    setRows((prev) => prev.map((r) => (r.part_number === saved.part_number
      ? { ...saved, open_lines: r.open_lines }
      : r)));
  };

  const filtered = Boolean(debouncedSearch || debouncedSupplierSearch || supplierFilterKey || firmSource !== 'all');

  return (
    <div className="space-y-6">
      <PartScheduleFiltersCard
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        supplierSearch={supplierSearch}
        onSupplierSearchChange={(v) => { setSupplierSearch(v); setPage(1); }}
        supplierOptions={supplierOptions}
        supplierFilter={supplierFilter}
        onSupplierFilterChange={(sel) => { setSupplierFilter(sel); setPage(1); }}
        statusLabel="Firm days"
        statusValue={firmSource}
        statusOptions={FIRM_SOURCE_OPTIONS}
        onStatusChange={(v) => { setFirmSource(v); setPage(1); }}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Parts without an ERP firm window</CardTitle>
          <p className="text-xs text-muted-foreground">
            {loading ? 'Loading…' : `${total.toLocaleString()} part${total === 1 ? '' : 's'} on open scheduling-agreement lines`}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error} <button onClick={() => void load()} className="underline ml-1">Retry</button>
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {filtered ? 'No parts match these filters.' : 'Every part on an open scheduling-agreement line has an ERP firm window.'}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Part</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">Open lines</TableHead>
              <TableHead className="text-right">Firm days</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Set by</TableHead>
              {canEdit && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.customer}-${r.part_number}`} data-testid="not-from-erp-row">
                <TableCell className="font-mono text-sm">{r.part_number}</TableCell>
                <TableCell className="text-sm max-w-[320px] truncate" title={r.description}>{r.description || '—'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.suppliers.length ? r.suppliers.join(', ') : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{r.open_lines}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{r.effective_firm_days ?? '—'}</TableCell>
                <TableCell>
                  {r.firm_source === 'manual'
                    ? <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">Manual</Badge>
                    : <Badge variant="secondary" title="Tenant default — no firm window from the ERP and none set here">Default</Badge>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {r.firm_source === 'manual'
                    ? <>{r.manual_set_by_name || '—'}{r.manual_set_at ? <> · {formatDate(r.manual_set_at)}</> : null}</>
                    : '—'}
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)} title="Set firm days for this part" aria-label={`Set firm days for ${r.part_number}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {total > PAGE_SIZE && (
        <PaginationControls page={page} totalPages={totalPages} disabled={loading} onChange={setPage} />
      )}
        </CardContent>
      </Card>

      <ManualFirmDaysDialog open={editing !== null} part={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
    </div>
  );
}
